<script lang="ts" module>
  export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'pause';
</script>

<script lang="ts">
  // Small status pill.
  //   <Badge tone="success" dot>Betaald</Badge>
  //   <Badge tone="brand" icon={Sparkles} size="sm">Nieuw</Badge>
  //   <Badge tone="warning" onclick={cycle} tooltip="Klik om te wijzigen">Open</Badge>   (renders a <button>)
  // Domain badges (PaymentBadge, support StatusBadge) map a status to a tone and
  // render this component; never copy the pill CSS.
  import type { Component, Snippet } from 'svelte';
  import { tooltip as tip } from './actions';

  interface Props {
    tone?: BadgeTone;
    size?: 'xs' | 'sm' | 'md';
    /** soft = tinted (default), outline = border only, solid = filled. */
    /** 'plain': a coloured dot and normal text, no background (statuses in lists). */
    variant?: 'soft' | 'outline' | 'solid' | 'plain';
    dot?: boolean;
    icon?: Component<any>;
    children: Snippet;
    title?: string;
    /** Tooltip text (also used as aria-label when there is no ariaLabel). */
    tooltip?: string;
    ariaLabel?: string;
    /** Makes the badge a button. */
    onclick?: (event: MouseEvent) => void;
    /** Hover styling without a click handler (e.g. inside a clickable row). */
    interactive?: boolean;
    disabled?: boolean;
    class?: string;
  }
  let {
    tone = 'neutral',
    size = 'md',
    variant = 'soft',
    dot = false,
    icon: Icon,
    children,
    title,
    tooltip,
    ariaLabel,
    onclick,
    interactive = false,
    disabled = false,
    class: cls = ''
  }: Props = $props();
  const iconSize = $derived(size === 'md' ? 13 : 12);
</script>

{#snippet inner()}
  {#if dot}<span class="dot" aria-hidden="true"></span>{/if}
  {#if Icon}<Icon size={iconSize} aria-hidden="true" />{/if}
  {@render children()}
{/snippet}

{#if onclick}
  <button
    type="button"
    class="badge {tone} {size} {variant} interactive {cls}"
    {title}
    {disabled}
    aria-label={ariaLabel}
    use:tip={{ text: tooltip, describe: true }}
    {onclick}
  >
    {@render inner()}
  </button>
{:else}
  <span class="badge {tone} {size} {variant} {cls}" class:interactive {title} aria-label={ariaLabel} use:tip={{ text: tooltip, describe: true }}>
    {@render inner()}
  </span>
{/if}

<style>
  .badge {
    --tone: var(--text-2);
    --tone-soft: var(--surface-3);
    --tone-text: var(--text-2);
    display: inline-flex;
    align-items: center;
    gap: var(--space-1_5);
    height: 24px;
    padding: 0 var(--space-2_5);
    border: 1px solid transparent;
    border-radius: var(--radius-pill);
    background: var(--tone-soft);
    color: var(--tone-text);
    font: inherit;
    font-size: var(--text-xs);
    font-weight: var(--weight-semibold);
    line-height: var(--leading-none);
    white-space: nowrap;
    max-width: 100%;
    flex-shrink: 0;
    transition: filter var(--dur), transform var(--dur-fast), background var(--dur), border-color var(--dur);
  }
  .sm { height: 20px; padding: 0 var(--space-2); font-size: var(--text-2xs); gap: var(--space-1); }
  .xs { height: 16px; padding: 0 var(--space-1_5); font-size: var(--text-2xs); gap: var(--space-1); }
  .dot { width: 6px; height: 6px; border-radius: var(--radius-full); background: var(--tone); flex-shrink: 0; }
  .badge :global(svg) { width: var(--icon-xs); height: var(--icon-xs); flex-shrink: 0; }

  .neutral { --tone: var(--text-3); --tone-soft: var(--surface-3); --tone-text: var(--text-2); }
  .brand { --tone: var(--brand); --tone-soft: var(--brand-soft); --tone-text: var(--brand); }
  .success { --tone: var(--success); --tone-soft: var(--success-soft); --tone-text: var(--success-strong); }
  .warning { --tone: var(--warning); --tone-soft: var(--warning-soft); --tone-text: var(--warning-strong); }
  .danger { --tone: var(--danger); --tone-soft: var(--danger-soft); --tone-text: var(--danger-strong); }
  .info { --tone: var(--info); --tone-soft: var(--info-soft); --tone-text: var(--info-strong); }
  .pause { --tone: var(--pause); --tone-soft: var(--pause-soft); --tone-text: var(--pause-strong); }

  .outline { background: transparent; border-color: color-mix(in srgb, var(--tone) 40%, transparent); }
  .solid { background: var(--tone); color: var(--on-brand); }
  .solid .dot { background: var(--on-brand); }
  .plain { background: transparent; color: var(--text-2); font-weight: var(--weight-medium); padding: 0; height: auto; }
  .plain.interactive { padding: var(--space-0_5) var(--space-1_5); margin: calc(-1 * var(--space-0_5)) calc(-1 * var(--space-1_5)); border-radius: var(--radius-xs); }
  .plain.interactive:hover:not(:disabled) { background: var(--surface-hover); filter: none; }

  button.badge { cursor: pointer; }
  .interactive:hover:not(:disabled) { filter: brightness(0.96); }
  :global([data-theme='dark']) .interactive:hover:not(:disabled) { filter: brightness(1.15); }
  button.badge:active:not(:disabled) { transform: scale(0.97); }
  button.badge:disabled { cursor: default; opacity: 0.7; }
  button.badge:focus-visible { outline: none; box-shadow: var(--ring); }
</style>
